import React, { useEffect, useState } from 'react';
import { Drawer, Space, Typography, Select, InputNumber, Button, Alert, Input, message } from 'antd';
import { ExportOutlined, SafetyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMultiWallet } from '../hooks/useMultiWallet';
import { createOvernightWithdrawal } from '../helpers/api';
import { fetchIdentities } from '../services/auth';
import { NETWORKS, CURRENCIES, SupportedNetwork, SupportedCurrency, currencyIcon, currencyLabel, formatAmount } from '../constants/networks';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  activeNetwork: SupportedNetwork;
  /** Рабочие балансы по валютам. */
  assets: Record<string, number>;
  onSuccess?: () => void;
  /** В карточке кабинета KredaFi вместо нижнего Drawer. */
  inline?: boolean;
}

/**
 * Вывод с рабочего баланса на подключённый кошелёк выбранной сети (вне Telegram).
 * Лимиты считает сервер (overnight/create-withdrawal.php): не более 20 USDT за
 * заявку, 200 в сутки, 10% баланса в месяц; заявку исполняет админ.
 */
function describeWithdrawError(err: any, t: (key: string) => string): string {
  const data = err?.response?.data;
  const raw =
    (data && typeof data === 'object' && (data.error || data.message)) ||
    (typeof err?.message === 'string' ? err.message : '') ||
    (typeof data === 'string' ? data : '');
  const code = String(raw || '').trim();
  if (code === 'telegram_required') return t('Link your Telegram account to withdraw funds');
  if (code === 'Incorrect wallet address' || code === 'Invalid parameters') {
    return t('Please connect your wallet address');
  }
  if (code === 'Currency is not available on this network') {
    return t('Currency is not available on this network');
  }
  if (code === 'Unauthorized') return t('Auth required');
  if (/monthly/i.test(code) || /10%/.test(code)) {
    return t('Amount exceeds monthly withdrawal limit (10% of balance)');
  }
  if (/daily/i.test(code) || /insufficient/i.test(code)) {
    return t('Insufficient funds');
  }
  if (code && code !== 'Failed to create withdrawal' && !/^Error:/.test(code) && code.length < 180) {
    return code;
  }
  return t('Failed to create withdrawal');
}

const WorkingBalanceWithdrawDrawer: React.FC<Props> = ({ open, onClose, activeNetwork, assets, onSuccess, inline = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wallet = useMultiWallet();
  const meta = NETWORKS[activeNetwork];
  const [currency, setCurrency] = useState<SupportedCurrency>(meta.tokens[0]);
  const [amount, setAmount] = useState<number | null>(null);
  const [address, setAddress] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [telegramRequired, setTelegramRequired] = useState(false);

  const connectedAddress = wallet.addresses[activeNetwork] || wallet.linkedAddresses[activeNetwork] || '';

  useEffect(() => {
    setCurrency(meta.tokens[0]);
    setAmount(null);
  }, [meta]);

  // Адрес получателя — из подключённого или привязанного кошелька ЭТОЙ сети.
  // При смене сети поле обязано очиститься: раньше адрес прошлой сети
  // оставался в поле, и заявка на Polygon могла уйти с TON-адресом.
  useEffect(() => {
    setAddress(connectedAddress || '');
  }, [connectedAddress, activeNetwork, open]);

  useEffect(() => {
    if (!open) return;
    fetchIdentities()
      .then((d) => setTelegramRequired(Boolean(d.payouts_blocked)))
      .catch(() => setTelegramRequired(false));
  }, [open]);

  const totalBalance = Number(assets[currency] ?? 0);
  const decimals = CURRENCIES[currency]?.displayDecimals ?? 2;
  const monthlyLimit = Number((totalBalance * 0.10).toFixed(decimals));

  const submit = async () => {
    if (!amount || amount <= 0) {
      message.error(t('Please enter a valid withdrawal amount'));
      return;
    }
    if (amount > totalBalance) {
      message.error(t('Insufficient funds'));
      return;
    }
    if (amount > monthlyLimit) {
      message.error(t('Amount exceeds monthly withdrawal limit (10% of balance)'));
      return;
    }
    if (!address.trim()) {
      message.error(t('Please connect your wallet address'));
      return;
    }
    setLoading(true);
    try {
      const res = await createOvernightWithdrawal({ currency, amount, wallet_address: address.trim(), network: activeNetwork });
      if (res?.success || res?.withdrawal_id) {
        // Сервер режет заявку по своим лимитам (не больше 20 USDT за раз,
        // 200 в сутки, 10% баланса в месяц) и возвращает то, что реально
        // принял. Раньше об этом никто не говорил: человек просил 100 USDT,
        // видел «заявка отправлена» и ждал 100, а в выплату уходило 20.
        const accepted = Number(res?.amount);
        if (Number.isFinite(accepted) && accepted > 0 && accepted < amount - 1e-9) {
          message.warning(
            `${t('Withdrawal request submitted successfully')}: ${formatAmount(currency, accepted)} ${currencyLabel(currency)}. ${t('The amount was reduced to the current limit.')}`,
            6
          );
        } else {
          message.success(t('Withdrawal request submitted successfully'));
        }
        setAmount(null);
        onSuccess?.();
        onClose();
      } else {
        message.error(describeWithdrawError({ message: res?.error, response: { data: res } }, t));
      }
    } catch (e: any) {
      message.error(describeWithdrawError(e, t));
    } finally {
      setLoading(false);
    }
  };

  const chipItems = [
    { label: t('{{percent}}% of limit', { percent: 25 }), ratio: 0.25 },
    { label: t('{{percent}}% of limit', { percent: 50 }), ratio: 0.50 },
    { label: t('{{percent}}% of limit', { percent: 75 }), ratio: 0.75 },
    { label: t('10% (Max)'), ratio: 1.0 },
  ];

  const form = (
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        {telegramRequired && (
          <Alert
            type="warning"
            showIcon
            message={t('Link your Telegram account to withdraw funds')}
            action={
              <Button size="small" type="primary" onClick={() => navigate('/settings/accounts')}>
                {t('Link Telegram')}
              </Button>
            }
            style={{ borderRadius: 12 }}
          />
        )}

        <div>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>{t('Currency')}</Text>
          <Select value={currency} onChange={(v: SupportedCurrency) => { setCurrency(v); setAmount(null); }} size="large" style={{ width: '100%' }}>
            {meta.tokens.map((c) => (
              <Select.Option key={c} value={c}>
                <Space size={6}>
                  <img src={currencyIcon(c)} alt={c} width={16} height={16} style={{ objectFit: 'contain' }} />
                  <Text strong>{currencyLabel(c)}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {t('available')}: {formatAmount(c, assets[c] ?? 0)}
                  </Text>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <div className={inline ? 'kf-label-row' : undefined}>
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: inline ? 0 : 6 }}>{t('Amount')} ({currencyLabel(currency)})</Text>
            {inline && (
              <span className="kf-limit">
                {t('Limit 10%')}: {formatAmount(currency, monthlyLimit)} {currencyLabel(currency)}
              </span>
            )}
          </div>
          <InputNumber value={amount} onChange={(v) => setAmount(v)} min={0} max={monthlyLimit || undefined} size="large" style={{ width: '100%' }} />
          <div className={inline ? 'kf-chips' : undefined} style={inline ? undefined : { display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
            {chipItems.map((item) => {
              const next = Number((monthlyLimit * item.ratio).toFixed(decimals));
              const selected = amount !== null && next === amount;
              return inline ? (
                <button
                  key={item.label}
                  type="button"
                  className={`kf-chip${selected ? ' on' : ''}`}
                  disabled={!monthlyLimit || monthlyLimit <= 0}
                  onClick={() => setAmount(next)}
                >
                  {item.label}
                </button>
              ) : (
                <Button
                  key={item.label}
                  size="small"
                  disabled={!monthlyLimit || monthlyLimit <= 0}
                  onClick={() => setAmount(next)}
                  style={{ borderRadius: '8px', fontSize: 12, borderColor: selected ? meta.color : undefined, color: selected ? meta.color : undefined }}
                >
                  {item.label}
                </Button>
              );
            })}
          </div>
          <div className={inline ? 'kf-meta' : undefined} style={inline ? undefined : { marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('Monthly limit (10%)')}: <Text strong style={{ color: '#16a34a' }}>{formatAmount(currency, monthlyLimit)} {currencyLabel(currency)}</Text>
            </Text>
            {' '}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('Total balance')}: {formatAmount(currency, totalBalance)} {currencyLabel(currency)}
            </Text>
          </div>
        </div>

        <div>
          <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
            {t('Wallet Address')} ({meta.name})
          </Text>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={connectedAddress ? '' : t('Connect your wallet')} size="large" />
          {!connectedAddress && (
            <Button type="link" size="small" onClick={() => wallet.connect(activeNetwork).catch(() => undefined)} style={{ paddingLeft: 0 }}>
              {t('Connect {{network}} wallet', { network: meta.name })}
            </Button>
          )}
        </div>

        {inline ? (
          <div className="kf-bankrun">
            <SafetyOutlined style={{ fontSize: 16, color: '#64748b', flexShrink: 0 }} />
            <div>
              <b>{t('Anti-bankrun protection')}</b>
              {': '}
              {t('Limits: up to 20 USDT per request and 10% of the wallet balance per month (Anti-bankrun). Requests are paid out within 24 hours.')}
            </div>
          </div>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 12, fontSize: 12 }}
            message={t('Limits: up to 20 USDT per request and 10% of the wallet balance per month (Anti-bankrun). Requests are paid out within 24 hours.')}
          />
        )}

        <Button
          type="primary"
          block
          size="large"
          loading={loading}
          disabled={telegramRequired}
          onClick={submit}
          icon={inline ? <ExportOutlined /> : undefined}
          style={{
            height: inline ? 50 : 48,
            borderRadius: inline ? 14 : 12,
            fontWeight: 600,
            fontSize: 15,
            background: inline ? '#1a73e8' : meta.color,
            borderColor: inline ? '#1a73e8' : meta.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <span>{t('Withdraw')} {amount ? `${amount} ${currencyLabel(currency)}` : ''}</span>
        </Button>
      </Space>
  );

  if (inline) {
    if (!open) return null;
    return (
      <div className="kf-panel">
        <div className="kf-panel-head">
          <h2 className="kf-panel-title">
            {t('Withdraw')} · {meta.name}
          </h2>
          <button type="button" className="kf-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {form}
      </div>
    );
  }

  return (
    <Drawer
      title={
        <Space size={8}>
          <img
            src={meta.icon}
            alt={meta.name}
            width={22}
            height={22}
            style={{ borderRadius: '50%', objectFit: 'contain' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <span>{t('Withdraw')} · {meta.name}</span>
        </Space>
      }
      placement="bottom"
      open={open}
      onClose={onClose}
      height="auto"
      bodyStyle={{ padding: '20px 16px 36px', borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '85vh', overflowY: 'auto' }}
    >
      {form}
    </Drawer>
  );
};

export default WorkingBalanceWithdrawDrawer;
